import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const ThemeCtx = createContext(null);
const THEMES = ["light", "dark", "system"];

const systemDark = () =>
  typeof window !== "undefined" && window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

function detect() {
  const s = localStorage.getItem("theme");
  return THEMES.includes(s) ? s : "system";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(detect);

  const apply = useCallback((th) => {
    const dark = th === "dark" || (th === "system" && systemDark());
    document.documentElement.classList.toggle("dark", dark);
  }, []);

  useEffect(() => { apply(theme); }, [theme, apply]);

  useEffect(() => {
    if (theme !== "system" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => apply("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme, apply]);

  const setTheme = useCallback((th) => {
    if (!THEMES.includes(th)) th = "system";
    setThemeState(th);
    localStorage.setItem("theme", th);
  }, []);

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
